import React from "react";
import { HelpCircle, Clock, UserMinus } from "lucide-react";

const CARDS = [
  {
    icon: HelpCircle,
    title: "They don't get answers",
    body: "A customer visits your site with a question. Nobody's there. They get confused. Confused people don't buy — they procrastinate. \"I'll come back later.\" They never come back.",
  },
  {
    icon: Clock,
    title: "You close at 5 PM. They don't.",
    body: "Most buying decisions happen outside business hours. Someone gets in a car accident at 9 PM and needs a lawyer. A bride is planning at midnight. A contractor needs an emergency quote on Sunday. You're not there.",
  },
  {
    icon: UserMinus,
    title: "They find a competitor instead.",
    body: "That customer still needs your service. They didn't stop wanting it. They just found someone else whose website gave them the information they needed to move forward.",
  },
];

export default function TheLeak() {
  return (
    <section data-testid="the-leak-section" className="bg-[#0A0F1E] fs-section">
      <div className="fs-container">
        <div className="max-w-3xl">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-8 bg-[#C9A84C]" />
            <span className="fs-tag">The Problem</span>
          </div>
          <h2 className="font-display text-4xl md:text-6xl uppercase tracking-[0.03em] leading-[0.95] text-white">
            Your Website Has a<br />
            <span className="text-[#C9A84C]">Revenue Leak.</span>
          </h2>
          <p className="mt-6 text-lg text-white/65 max-w-2xl leading-relaxed">
            And you probably don't know it's happening. Every day, qualified
            buyers visit your website, hit a wall, and quietly leave to find
            someone else.
          </p>
        </div>

        <div className="mt-16 grid md:grid-cols-3 gap-6 lg:gap-8">
          {CARDS.map((c, i) => (
            <article key={i} data-testid={`leak-card-${i}`} className="fs-card">
              <div className="h-11 w-11 rounded-md border border-[#C9A84C]/35 flex items-center justify-center text-[#C9A84C]">
                <c.icon size={20} strokeWidth={1.6} />
              </div>
              <h3 className="mt-6 font-display text-2xl tracking-[0.04em] uppercase text-white">
                {c.title}
              </h3>
              <p className="mt-3 text-sm text-white/65 leading-relaxed">{c.body}</p>
            </article>
          ))}
        </div>

        {/* Conversion callout */}
        <div
          data-testid="conversion-callout"
          className="mt-16 rounded-2xl border border-[#C9A84C]/30 bg-[#131929] text-white px-8 md:px-14 py-12 md:py-14 grid md:grid-cols-12 gap-8 items-center"
        >
          <div className="md:col-span-4 flex md:block items-baseline gap-3">
            <div className="font-display text-7xl md:text-[9rem] leading-none text-[#C9A84C] tracking-[0.02em]">
              50%
            </div>
            <div className="text-[0.7rem] uppercase tracking-[0.22em] text-white/55 mt-2 md:mt-4">
              Conversion benchmark
            </div>
          </div>
          <div className="md:col-span-8">
            <p className="font-display text-3xl md:text-4xl uppercase tracking-[0.03em] leading-[1.05]">
              of your website visitors should be converting.
            </p>
            <p className="mt-5 text-white/70 leading-relaxed">
              A qualified lead doesn't visit a website randomly. They're there
              because they have a problem and your business piqued their
              interest. That's already a potential buyer. If you're not
              converting them, it's because they didn't get the clarity they
              needed to take the next step — and there was nobody there to
              guide them.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
